import { useMemo, useState, type CSSProperties } from 'react';

import type { FirewallOverview, GroupView, RuleView, SystemView } from './types';

type Filter = 'all' | 'allow' | 'block' | 'baseline' | 'attention';

/** Entity colours from the design tokens, so a group reads as its own colour everywhere. */
const GROUP_TONES = ['e-product', 'e-container', 'e-llm', 'e-endpoint', 'e-client'] as const;

function day(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—';
}

interface Band {
  key: string;
  kind: 'subnet' | 'group' | 'system';
  title: string;
  sub: string;
  tone: string;
  members: SystemView[];
  rules: RuleView[];
}

export function RulesTable({
  overview,
  canManage,
  onAdd,
  onEdit,
  onToggle,
  onDelete,
}: {
  overview: FirewallOverview;
  canManage: boolean;
  onAdd: () => void;
  onEdit: (rule: RuleView) => void;
  onToggle: (rule: RuleView) => void;
  onDelete: (rule: RuleView) => void;
}) {
  const [filter, setFilter] = useState<Filter>('all');

  const visible = useMemo(
    () =>
      overview.rules.filter((r) => {
        if (filter === 'allow') return r.action === 'ALLOW';
        if (filter === 'block') return r.action === 'DENY';
        if (filter === 'baseline') return r.source === 'baseline';
        if (filter === 'attention') return r.expired || r.reviewOverdue || !r.enabled;
        return true;
      }),
    [overview.rules, filter],
  );

  const bands = useMemo<Band[]>(() => {
    const byId = new Map(overview.systems.map((s) => [s.id, s]));
    const out: Band[] = [];
    const subnetRules = visible.filter((r) => r.scope === 'SUBNET');
    out.push({ key: 'subnet', kind: 'subnet', title: 'Every system', sub: overview.subnet, tone: 'accent', members: [], rules: subnetRules });

    overview.groups.forEach((g: GroupView, i) => {
      const members = g.memberIds.map((id) => byId.get(id)).filter((s): s is SystemView => s !== undefined);
      out.push({
        key: `group:${g.id}`,
        kind: 'group',
        title: g.name,
        sub: g.description ?? '',
        tone: GROUP_TONES[i % GROUP_TONES.length],
        members,
        rules: visible.filter((r) => r.scope === 'GROUP' && r.targetId === g.id),
      });
    });

    for (const s of overview.systems) {
      const rules = visible.filter((r) => r.scope === 'SERVER' && r.targetId === s.id);
      if (rules.length === 0) continue;
      out.push({ key: `system:${s.id}`, kind: 'system', title: s.name, sub: s.address ?? '', tone: 'e-server', members: [], rules });
    }
    return out;
  }, [visible, overview.systems, overview.groups, overview.subnet]);

  const totalRules = overview.rules.length;
  const baselineCount = overview.rules.filter((r) => r.source === 'baseline').length;

  return (
    <section className="ciq-rules" aria-label="Firewall rules">
      <header className="ciq-rules-head">
        <div>
          <h3>Rules</h3>
          <p className="ciq-muted small">
            {totalRules} rule{totalRules === 1 ? '' : 's'} · {baselineCount} from the suggested baseline · grouped by what they apply to
          </p>
        </div>
        <div className="ciq-actions">
          <div className="ciq-seg" role="radiogroup" aria-label="Filter rules">
            {(
              [
                ['all', 'All'],
                ['allow', 'Allow'],
                ['block', 'Block'],
                ['baseline', 'Baseline'],
                ['attention', 'Needs attention'],
              ] as const
            ).map(([value, label]) => (
              <button key={value} type="button" role="radio" aria-checked={filter === value} className={filter === value ? 'on' : ''} onClick={() => setFilter(value)}>
                {label}
              </button>
            ))}
          </div>
          {canManage && (
            <button type="button" onClick={onAdd}>
              Add rule
            </button>
          )}
        </div>
      </header>

      <div className="table-scroll">
        <table className="data-table ciq-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Action</th>
              <th>Port</th>
              <th>From</th>
              <th>Applies to</th>
              <th>Expires · review</th>
              <th>Source</th>
              <th>On</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          {bands.map((band) => (
            <tbody key={band.key} className="ciq-band-body">
              <tr className="ciq-band-row" style={{ '--band': `var(--${band.tone})` } as CSSProperties}>
                <td colSpan={9}>
                  <div className="ciq-band">
                    <span className="ciq-band-kind">
                      {band.kind === 'subnet' ? 'Subnet' : band.kind === 'group' ? 'Group' : 'System'}
                    </span>
                    <span className="ciq-band-name">{band.title}</span>
                    {band.kind === 'group' && (
                      <span className="ciq-band-members">
                        {band.members.length === 0 ? (
                          <span className="ciq-muted small">No members yet</span>
                        ) : (
                          band.members.map((m) => (
                            <span key={m.id} className="ciq-chip">
                              {m.name} <span className="mono ciq-muted">{m.address ?? ''}</span>
                            </span>
                          ))
                        )}
                      </span>
                    )}
                    {band.kind !== 'group' && band.sub && <span className="ciq-muted small mono">{band.sub}</span>}
                    <span className="ciq-band-count">
                      {band.rules.length} rule{band.rules.length === 1 ? '' : 's'}
                    </span>
                  </div>
                </td>
              </tr>

              {band.rules.length === 0 && (
                <tr className="ciq-empty-row">
                  <td colSpan={9} className="ciq-muted small">
                    {band.kind === 'group'
                      ? 'No rules for this group yet. Enable a suggestion below or add a rule.'
                      : 'No rules here.'}
                  </td>
                </tr>
              )}

              {band.rules.map((r) => (
                <RuleRow key={r.id} rule={r} tone={band.tone} canManage={canManage} onEdit={onEdit} onToggle={onToggle} onDelete={onDelete} />
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
}

function RuleRow({
  rule,
  tone,
  canManage,
  onEdit,
  onToggle,
  onDelete,
}: {
  rule: RuleView;
  tone: string;
  canManage: boolean;
  onEdit: (rule: RuleView) => void;
  onToggle: (rule: RuleView) => void;
  onDelete: (rule: RuleView) => void;
}) {
  const allow = rule.action === 'ALLOW';
  const status = rule.isSystem
    ? { tone: 'system', text: 'System guard' }
    : !rule.enabled
      ? { tone: 'off', text: 'Disabled' }
      : rule.expired
        ? { tone: 'bad', text: 'Expired' }
        : rule.reviewOverdue
          ? { tone: 'warn', text: 'Review due' }
          : rule.applied
            ? { tone: 'ok', text: 'Active' }
            : { tone: 'off', text: 'Not applied' };
  const editable = canManage && !rule.isSystem;

  return (
    <tr className={rule.enabled ? 'ciq-rule-row' : 'ciq-rule-row is-off'} style={{ '--band': `var(--${tone})` } as CSSProperties}>
      <td>
        <span className="ciq-status small-status" data-tone={status.tone}>
          <span aria-hidden="true">
            {status.tone === 'ok' ? '●' : status.tone === 'bad' ? '■' : status.tone === 'warn' ? '▲' : status.tone === 'system' ? '◆' : '○'}
          </span>{' '}
          {status.text}
        </span>
      </td>
      <td>
        <span className={allow ? 'ciq-verb allow' : 'ciq-verb block'}>{allow ? 'Allow' : 'Block'}</span>
      </td>
      <td>
        <span className="ciq-chip mono">{rule.portLabel}</span>
      </td>
      <td>
        {rule.sources.length ? (
          <span className="ciq-chips">
            {rule.sources.map((s) => (
              <span key={s} className="ciq-chip mono">
                {s}
              </span>
            ))}
          </span>
        ) : (
          <span className="ciq-muted small">anywhere</span>
        )}
      </td>
      <td>{rule.scope === 'SUBNET' ? 'Every system' : rule.targetName}</td>
      <td className="small">
        {allow ? (
          <>
            <div>{day(rule.expiresAt)}</div>
            <div className="ciq-muted">review {day(rule.reviewBy)}</div>
          </>
        ) : (
          <span className="ciq-muted">no expiry</span>
        )}
      </td>
      <td>
        {rule.source === 'baseline' ? (
          <span className="ciq-badge" title={rule.baselineTitle ?? ''}>
            Suggested · {rule.baselineTitle}
          </span>
        ) : (
          <span className="ciq-muted small">Written by hand</span>
        )}
      </td>
      <td>
        {editable ? (
          <button type="button" role="switch" aria-checked={rule.enabled} aria-label={rule.enabled ? 'Disable rule' : 'Enable rule'} className={rule.enabled ? 'ciq-switch small on' : 'ciq-switch small'} onClick={() => onToggle(rule)}>
            <span className="ciq-switch-knob" aria-hidden="true" />
          </button>
        ) : (
          <span className="ciq-muted small">{rule.enabled ? 'On' : 'Off'}</span>
        )}
      </td>
      <td>
        {editable && (
          <span className="ciq-actions">
            <button type="button" className="ghost small-btn" onClick={() => onEdit(rule)}>Edit</button>
            <button type="button" className="ghost small-btn" onClick={() => onDelete(rule)}>Delete</button>
          </span>
        )}
      </td>
    </tr>
  );
}
